import React, { FC, useEffect } from 'react';
import { Form, Input, Modal as AntModal, Row } from 'antd';
import { EMAIL, ENTER_EMAIL } from '../../../utils/constants/labels';
import SafeAction from '../SafeAction';
import { api } from '../../../api/api';
import { Coach, User } from '../../../types/userTypes';
import { CopyOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import colors from '../../../utils/colors';
import { copyToClipboard, initStudentSession, NotificationType, notify } from '../../../utils/helpers';
import GiveAccessToCoach from '../../users/GiveAccessToCoach';
import Button from '../buttons/Button';

type Props = {
  user?: User | Coach;
  websiteSubdomain: string;
  onClose: () => void;
  onUserUpdate: Function;
};

const UserActionsModal: FC<Props> = ({ user, websiteSubdomain, onClose, onUserUpdate }: Props) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.resetFields();
    }
  }, [user]);

  const renderContent = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div style={{ borderBottom: '1px solid #e9e9e9', paddingBottom: '15px' }}>
          {
            (user as User)?.redeemAccessCode ? (
              <>
                <Row align={'middle'}>
                  <CopyOutlined
                    onClick={() => {
                      copyToClipboard(`https://${websiteSubdomain}.shovelapp.io/-/set-password/${(user as User)?.redeemAccessCode}`);
                      notify('Invitation link copied', NotificationType.SUCCESS);
                    }}
                  />
                  <div style={{ marginLeft: '8px' }}>
                    Copy activation link to clipboard
                  </div>
                </Row>
                <SafeAction
                  title={'Resend activation link'}
                  action={() => api.resendUserActivation(user?.id!)}
                  doneText={'Sent'}
                />
              </>
            ) : (
              <>
                <SafeAction
                  Icon={<KeyOutlined />}
                  action={async () => {
                    const res = await api.generateUserResetPasswordCode(user?.id!);
                    if (res.ok) {
                      // temporary fix
                      const link = res.data?.replace('/set-password', '/reset-password');
                      copyToClipboard(link);
                    }
                    return res;
                  }}
                  onError={(message: string) => notify(message)}
                  doneText={'Reset password code is copied'}
                />
                <div>
                  Generate new reset password code that lasts for 7 days and copy the link into clipboard. After
                  generating new code, the previous one is invalidated.
                </div>
              </>
            )
          }
        </div>
        <div style={{ borderBottom: '1px solid #e9e9e9', paddingBottom: '15px' }}>
          <Form
            form={form}
            style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}
            initialValues={{ email: user?.email }}
          >
            <Form.Item style={{ width: '100%' }} name='email' label={EMAIL} rules={[{ required: true, type: 'email' }]}>
              <Input type={'email'} placeholder={ENTER_EMAIL} />
            </Form.Item>
            <SafeAction
              title={'Save'}
              onError={(message: string) => notify(message)}
              action={() => form
                .validateFields()
                .then(async values => {
                  const res = await api.changeUserEmail(user?.id!, values.email);
                  if (res.ok) {
                    onUserUpdate();
                  }
                  return res;
                })
                .catch(info => {
                  console.log('Validate Failed:', info);
                  return { ok: false, data: { message: 'Invalid email format' } }
                })

              }
              doneText={'Email updated'}
            />
          </Form>
          <div>
            Update user's email. Email is automatically marked as verified.
          </div>
        </div>
        <div style={{ borderBottom: '1px solid #e9e9e9', paddingBottom: '15px' }}>
          <SafeAction
            Icon={<DeleteOutlined style={{ color: colors.negative }} />}
            action={async () => {
              const res = await api.deleteAccount(user?.id!);
              if (res.ok) {
                onUserUpdate();
              }
              return res;
            }}
            onError={(message: string) => notify(message)}
            doneText={'User is deleted'}
          />
          <div>
            Delete user's account, same as he did it from the settings section. This action cannot be undone.
          </div>
        </div>
        {(user as Coach)?.emailVerificationCode && (
          <div style={(user as User)?.role === 'STUDENT' ? { borderBottom: '1px solid #e9e9e9', paddingBottom: '15px' } : {}}>
            <Row align={'middle'}>
              <CopyOutlined
                onClick={() => {
                  copyToClipboard(`https://coach.shovelapp.io/verify-email/${(user as Coach)?.emailVerificationCode}`);
                  notify('Email verification link copied', NotificationType.SUCCESS);
                }}
              />
              <div style={{ marginLeft: '8px' }}>
                Copy email verification link to clipboard
              </div>
            </Row>
            <SafeAction
              title={'Resend email verification link'}
              action={() => api.resendUserEmailVerification(user?.id!)}
              doneText={'Sent'}
            />
          </div>
        )}
        {
          (user as User)?.role === 'STUDENT' && (
            <Row style={{ borderBottom: '1px solid #e9e9e9' }}>
              <GiveAccessToCoach user={user as User} />
            </Row>
          )
        }
        {
          (user as User)?.role === 'STUDENT' && (
            <Button onClick={() => initStudentSession(user?.id!)}>
              Start Session
            </Button>
          )
        }
      </div>
    )
  }
  return (
    <AntModal
      title={''}
      visible={!!user}
      footer={null}
      onCancel={() => onClose()}
    >
      {!!user && renderContent()}
    </AntModal>
  );
};

export default UserActionsModal;
